import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Loader2, Package, Truck, Shirt, CalendarPlus, CalendarClock, ShieldCheck, CheckCircle2 } from "lucide-react";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { api } from "@/lib/api";

const ICONS = {
  totes_10: Package,
  dolly: Truck,
  wardrobe: Shirt,
  extension_3: CalendarClock,
  extension_7: CalendarPlus,
  damage_protection: ShieldCheck,
};

export default function Upsell() {
  const { code } = useParams();
  const [booking, setBooking] = useState(null);
  const [addons, setAddons] = useState([]);
  const [qty, setQty] = useState({}); // { addon_id: number }
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(false);
  const [justPurchased, setJustPurchased] = useState(null); // { items, total }

  useEffect(() => {
    let cancelled = false;
    Promise.all([api.get(`/bookings/${code}`), api.get(`/addons`)])
      .then(([b, a]) => {
        if (cancelled) return;
        setBooking(b.data);
        setAddons(a.data);
      })
      .catch(() => !cancelled && setError(true));
    return () => { cancelled = true; };
  }, [code]);

  const setItemQty = (id, n) => setQty((q) => ({ ...q, [id]: Math.max(0, n) }));

  const selected = useMemo(
    () => addons
      .filter((a) => (qty[a.id] || 0) > 0)
      .map((a) => ({ addon: a, quantity: qty[a.id] })),
    [addons, qty]
  );
  const totalNow = selected.reduce((s, { addon, quantity }) => s + addon.price * quantity, 0);

  const purchase = async () => {
    if (selected.length === 0) return;
    setSubmitting(true);
    try {
      const items = selected.map(({ addon, quantity }) => ({ addon_id: addon.id, quantity }));
      const { data } = await api.post(`/bookings/${code}/upsell`, { items });
      setBooking(data);
      setJustPurchased({ items: selected, total: totalNow });
      setQty({});
      toast.success(`Charged $${totalNow} to your saved card`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not process add-ons");
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 bg-[#F8F8F8]">
        <p>Booking not found.</p>
        <Link to="/" className="bg-[#FF4F00] text-white px-6 py-3 font-semibold">Home</Link>
      </div>
    );
  }
  if (!booking || addons.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F8F8]">
        <Loader2 className="w-6 h-6 animate-spin text-[#FF4F00]" />
      </div>
    );
  }

  const hasSavedCard = !!booking.stripe_customer_id && !!booking.stripe_payment_method_id;

  return (
    <div className="bg-[#F8F8F8] min-h-screen" data-testid="upsell-page">
      <Nav />
      <section className="pt-24 md:pt-28 pb-24">
        <div className="max-w-[1100px] mx-auto px-6 md:px-12 lg:px-16">
          <Link
            to={`/confirmation/${code}`}
            className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-[#FF4F00] mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back to your booking
          </Link>

          <div className="flex items-center gap-2 mb-6">
            <span className="w-8 h-px bg-[#FF4F00]" />
            <span className="text-xs tracking-[0.25em] uppercase text-neutral-600 font-semibold">
              Add-ons · Booking {booking.confirmation_code}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold leading-[0.95] tracking-tight max-w-2xl">
            Need a little more?
          </h1>
          <p className="mt-4 text-neutral-600 max-w-lg leading-relaxed">
            Add extras to your <span className="font-semibold text-[#0A0A0A]">{booking.tier_name}</span> delivery
            for {booking.start_date} → {booking.end_date}. Charged to the card you already have on file — no re-entry needed.
          </p>

          {!hasSavedCard && (
            <div className="mt-6 bg-amber-50 border border-amber-200 p-4 text-sm text-amber-800 max-w-lg">
              We don&apos;t have a saved card on file for this booking yet. Please complete your deposit checkout first.
            </div>
          )}

          {justPurchased && (
            <div className="mt-8 bg-emerald-50 border border-emerald-200 p-5 flex items-start gap-3" data-testid="upsell-success">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5" />
              <div className="text-sm text-emerald-900">
                <p className="font-semibold">${justPurchased.total} charged. You&apos;re all set.</p>
                <p className="mt-1 text-emerald-700">
                  Added: {justPurchased.items.map(({ addon, quantity }) => `${quantity}× ${addon.name}`).join(" · ")}. They&apos;ll arrive with your delivery.
                </p>
              </div>
            </div>
          )}

          {/* Existing add-ons on this booking */}
          {booking.addons && booking.addons.length > 0 && (
            <div className="mt-10 bg-white border border-neutral-200 p-6 md:p-8">
              <p className="text-xs tracking-[0.25em] uppercase text-neutral-500 font-semibold">
                Already added ({booking.addons.length} order{booking.addons.length > 1 ? "s" : ""})
              </p>
              <ul className="mt-4 space-y-2 text-sm">
                {booking.addons.flatMap((batch, bi) =>
                  batch.items.map((it, ii) => (
                    <li key={`${bi}-${ii}`} className="flex justify-between text-neutral-700">
                      <span>{it.quantity}× {it.name}</span>
                      <span className="font-semibold">${it.line_total}</span>
                    </li>
                  ))
                )}
                <li className="flex justify-between pt-3 border-t border-neutral-200 text-[#0A0A0A]">
                  <span className="font-semibold">Add-ons total</span>
                  <span className="font-display text-lg font-bold">${booking.addon_total}</span>
                </li>
              </ul>
            </div>
          )}

          {/* Add-on grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-10">
            {addons.map((addon) => {
              const Icon = ICONS[addon.id] || Package;
              const q = qty[addon.id] || 0;
              const alreadyActive = addon.id === "damage_protection" && booking.damage_protection;
              return (
                <div
                  key={addon.id}
                  data-testid={`addon-${addon.id}`}
                  className={`bg-white border p-6 md:p-7 flex flex-col ${
                    alreadyActive
                      ? "border-neutral-200 opacity-60"
                      : q > 0
                        ? "border-[#FF4F00] ring-1 ring-[#FF4F00]"
                        : "border-neutral-200"
                  }`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="w-11 h-11 bg-[#FF4F00]/10 text-[#FF4F00] flex items-center justify-center">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div className="text-right">
                      <p className="font-display text-2xl font-bold">${addon.price}</p>
                      <p className="text-xs text-neutral-500 tracking-wide uppercase">per {addon.unit}</p>
                    </div>
                  </div>
                  <h3 className="font-display text-xl font-bold mt-4">{addon.name}</h3>
                  <p className="text-sm text-neutral-600 mt-2 flex-1">{addon.description}</p>

                  {alreadyActive ? (
                    <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-emerald-700">
                      <CheckCircle2 className="w-4 h-4" /> Already active on this booking
                    </div>
                  ) : (
                    <div className="mt-6 flex items-center justify-between">
                      <div className="inline-flex items-center border border-neutral-300">
                        <button
                          type="button"
                          onClick={() => setItemQty(addon.id, q - 1)}
                          data-testid={`addon-decrement-${addon.id}`}
                          className="w-9 h-9 text-lg hover:bg-neutral-100 disabled:opacity-30"
                          disabled={q === 0}
                        >−</button>
                        <span
                          className="w-10 text-center text-sm font-bold"
                          data-testid={`addon-qty-${addon.id}`}
                        >{q}</span>
                        <button
                          type="button"
                          onClick={() => setItemQty(addon.id, q + 1)}
                          data-testid={`addon-increment-${addon.id}`}
                          className="w-9 h-9 text-lg hover:bg-neutral-100"
                        >+</button>
                      </div>
                      {q > 0 && (
                        <span className="text-sm font-semibold text-[#FF4F00]" data-testid={`addon-line-${addon.id}`}>
                          + ${addon.price * q}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Sticky checkout bar */}
          <div className="mt-10 bg-[#0A0A0A] text-white p-6 md:p-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <p className="text-xs tracking-[0.25em] uppercase text-neutral-400 font-semibold">Charging now</p>
              <p className="font-display text-3xl md:text-4xl font-bold mt-1" data-testid="upsell-total">
                ${totalNow}
              </p>
              {selected.length > 0 && (
                <p className="text-sm text-neutral-400 mt-2">
                  {selected.map(({ addon, quantity }) => `${quantity}× ${addon.name}`).join(" · ")}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={purchase}
              disabled={submitting || selected.length === 0 || !hasSavedCard}
              data-testid="upsell-submit"
              className="bg-[#FF4F00] text-white px-8 py-4 font-semibold tracking-wide hover:bg-[#E64700] transition-colors inline-flex items-center gap-3 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {submitting ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Charging saved card…</>
              ) : (
                <>Charge saved card <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
          </div>

          <p className="mt-4 text-xs text-neutral-500 flex items-center gap-2">
            <ShieldCheck className="w-3.5 h-3.5" />
            Charged to the card you used for your deposit. Fully refundable up to 24 h before delivery.
          </p>
        </div>
      </section>
      <Footer />
    </div>
  );
}
